import MenuListPopUp from 'components/MenuListPopUp/MenuListPopUp'
import TitleWithSubtitleCell from 'components/table/TitleWithSubtitleCell'
import { statusChecker } from 'components/StatusMessage/Status'
import { getFormattedDate, getTypeKey } from 'utils'
import { unknown } from 'utils/constants'
import type { ColDef, RowNode } from 'ag-grid-community'
import type { ExtractElementType, ProductsDetailDataV5 } from 'utils/utils.d'
import tableStyles from '../table/table.module.scss'

export type VariantDataType = ExtractElementType<NonNullable<ProductsDetailDataV5['variants']>>
export type VariantValidationStatusType = NonNullable<VariantDataType['validation']>['status']

export const masterSVG = (
  <svg width="11" height="10" viewBox="0 0 11 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5.4152 8.2671C5.32798 8.22138 5.22387 8.22138 5.13665 8.2671L2.64623 9.57251C2.42834 9.68672 2.17317 9.50456 2.21041 9.26139L2.64641 6.41421C2.66082 6.32006 2.62968 6.22466 2.56249 6.15716L0.616012 4.20161C0.442953 4.02774 0.540378 3.73096 0.782812 3.69349L3.61331 3.256C3.7096 3.24112 3.79266 3.18041 3.83606 3.09319L5.00735 0.739699C5.11772 0.51792 5.43413 0.517919 5.5445 0.739699L6.71579 3.09319C6.7592 3.18041 6.84226 3.24112 6.93854 3.256L9.76904 3.69349C10.0115 3.73096 10.1089 4.02774 9.93584 4.20161L7.98936 6.15716C7.92217 6.22466 7.89103 6.32006 7.90545 6.41421L8.34144 9.26139C8.37868 9.50456 8.12351 9.68672 7.90562 9.57251L5.4152 8.2671Z"
      fill="#121619"
    />
  </svg>
)

const sortVariants = (
  valueA: string,
  valueB: string,
  nodeA: RowNode<VariantDataType>,
  nodeB: RowNode<VariantDataType>,
): number => {
  // Master variant should always be first, followed by alphabetical order
  if (nodeA.data?.master) return -1
  if (nodeB.data?.master) return 1
  return valueA.localeCompare(valueB)
}

export const columnStructure = (
  onDeleteAction: (data: VariantDataType) => void,
  onSetMasterAction: (data: VariantDataType) => void,
): ColDef<VariantDataType>[] => [
  {
    headerName: 'Product',
    field: getTypeKey<VariantDataType>('reference_name'),
    sortable: true,
    flex: 1,
    resizable: true,
    sort: 'asc',
    sortingOrder: ['asc', 'desc'],
    comparator: sortVariants,
    cellRenderer: ({ data }: { data: VariantDataType }) => {
      return (
        <TitleWithSubtitleCell
          title={
            (
              <>
                {data.master && <>{masterSVG} </>}
                {data.reference_name}
              </>
            ) || unknown
          }
          subTitle={data.variant_id || unknown}
        />
      )
    },
  },
  {
    headerName: 'Image URL',
    field: 'image_url',
    sortable: false,
    width: 120,
    resizable: true,
    cellRenderer: ({ data }: { data: VariantDataType }) => {
      return !data.reference_image_url ? 'No' : 'Yes'
    },
  },
  {
    headerName: 'Variant attribute',
    field: 'variant_attribute',
    sortable: false,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: VariantDataType }) => {
      const outputAttributes = extractAttributes(data.attributes)

      return Object.values(outputAttributes)
        .filter(value => value !== undefined)
        .join(', ')
    },
  },
  {
    headerName: 'Last updated',
    field: getTypeKey<VariantDataType>('updated_at'),
    sortable: true,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: VariantDataType }) => (
      <TitleWithSubtitleCell
        title={getFormattedDate(data.updated_at)}
        subTitle={data?.update_info?.user_name || unknown}
      />
    ),
  },
  {
    headerName: 'Quality',
    field: 'quality',
    sortable: false,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: VariantDataType }) => {
      return statusChecker(data.validation?.status).tag
    },
  },
  {
    headerName: '',
    field: 'actions',
    width: 75,
    resizable: true,
    cellRenderer: ({ data }: { data: VariantDataType }) => {
      // Temporarily hiding delete option for master variants until backend protection is implemented
      // TODO: Remove this restriction once backend handles master variant deletion logic
      const menuItems = [
        // Only show 'Make master' option if it's not already master
        ...(!data.master
          ? [
              {
                label: 'Make master',
                onClick: () => onSetMasterAction(data),
              },
            ]
          : []),
        // Only show delete option if it's not a master variant
        ...(!data.master
          ? [
              {
                label: 'Delete',
                onClick: () => onDeleteAction(data),
              },
            ]
          : []),
      ].filter(Boolean)
      return (
        <MenuListPopUp
          theme="transparent-border"
          buttonSize="s"
          className={tableStyles.table_actions}
          menuItems={menuItems}
          data-testid="menu-actions"
        />
      )
    },
  },
]

interface ExtractedAttributes {
  quantity?: string
  size?: string
  colour?: string
  flavour?: string
  format?: string
}

function extractAttributes(attributes: VariantDataType['attributes']): ExtractedAttributes {
  const extractedAttributes: ExtractedAttributes = {}

  attributes?.forEach(attribute => {
    if (attribute.name && ['quantity', 'size', 'colour', 'flavour', 'format'].includes(attribute.name)) {
      extractedAttributes[attribute.name as keyof ExtractedAttributes] = attribute.value
    }
  })

  return extractedAttributes
}
